import type { HexGrid, HexCell, HexCoord, Puzzle, PuzzleDifficulty, PoolColor } from "../types.ts";
import { coordToKey, hexRange } from "./hex.ts";
import { applyMove, checkWin } from "./cascade.ts";
import { mulberry32, randInt, randPick, shuffle } from "./prng.ts";

const PALETTE: PoolColor[] = [
  "#ef4444", // red
  "#3b82f6", // blue
  "#22c55e", // green
  "#eab308", // yellow
];

export const DIFFICULTY_TIERS: Record<string, PuzzleDifficulty> = {
  easy:   { name: "easy",   radius: 2, numColors: 2, minCapacity: 2, maxCapacity: 3, optimalDrops: 3 },
  medium: { name: "medium", radius: 3, numColors: 3, minCapacity: 2, maxCapacity: 4, optimalDrops: 4 },
  hard:   { name: "hard",   radius: 3, numColors: 4, minCapacity: 2, maxCapacity: 5, optimalDrops: 5 },
  expert: { name: "expert", radius: 4, numColors: 4, minCapacity: 2, maxCapacity: 5, optimalDrops: 6 },
};

// Minimum acceptable optimal moves per tier
const MIN_OPTIMAL: Record<string, number> = {
  easy: 2,
  medium: 3,
  hard: 4,
  expert: 5,
};

const BFS_MAX_STATES = 150_000;

// ── Compact grid state key for BFS deduplication ──

function serializeGridState(grid: HexGrid): string {
  const parts: string[] = [];
  for (const [key, cell] of grid.cells) {
    parts.push(`${key}:${cell.color}:${cell.count}`);
  }
  parts.sort();
  return parts.join("|");
}

// ── BFS optimal solver (parent-pointer approach for efficiency) ──

interface BfsNode {
  grid: HexGrid;
  parentIdx: number; // -1 for root
  move: HexCoord | null;
}

/**
 * Find the minimum-move solution using level-by-level BFS.
 * Uses parent pointers instead of copying move arrays (much faster).
 */
function bfsSolve(grid: HexGrid, maxDepth: number): HexCoord[] | null {
  if (checkWin(grid)) return [];

  const coords = hexRange(grid.radius);
  const visited = new Set<string>();
  visited.add(serializeGridState(grid));

  const allNodes: BfsNode[] = [{ grid, parentIdx: -1, move: null }];
  let levelStart = 0;
  let levelEnd = 1;

  for (let depth = 0; depth < maxDepth; depth++) {
    const nextStart = allNodes.length;

    for (let i = levelStart; i < levelEnd; i++) {
      const node = allNodes[i]!;

      for (const coord of coords) {
        const result = applyMove(node.grid, coord);
        if (!result.success) continue;

        if (result.won) {
          // Reconstruct path
          const path: HexCoord[] = [coord];
          let idx = i;
          while (idx > 0) {
            path.unshift(allNodes[idx]!.move!);
            idx = allNodes[idx]!.parentIdx;
          }
          return path;
        }

        const stateKey = serializeGridState(result.finalGrid);
        if (visited.has(stateKey)) continue;
        visited.add(stateKey);

        if (visited.size >= BFS_MAX_STATES) return null;

        allNodes.push({ grid: result.finalGrid, parentIdx: i, move: coord });
      }
    }

    levelStart = nextStart;
    levelEnd = allNodes.length;
    if (levelStart === levelEnd) break;
  }

  return null;
}

// ── Random grid generation ──

function generateRandomGrid(
  rng: () => number,
  difficulty: PuzzleDifficulty,
  colors: PoolColor[],
  targetColor: PoolColor
): HexGrid {
  const coords = hexRange(difficulty.radius);
  const cells = new Map<string, HexCell>();

  for (const coord of coords) {
    const capacity = randInt(rng, difficulty.minCapacity, difficulty.maxCapacity);
    const color = randPick(rng, colors);
    // Cap count below capacity to prevent instant cascades
    const maxStart = Math.max(1, capacity - 1);
    const count = randInt(rng, 1, maxStart);
    cells.set(coordToKey(coord), { coord, color, count, capacity });
  }

  return { radius: difficulty.radius, cells, targetColor };
}

// ── Main generator ──

export function generatePuzzle(seed: number, difficulty: PuzzleDifficulty): Puzzle {
  const rng = mulberry32(seed);
  const colors = shuffle(rng, PALETTE).slice(0, difficulty.numColors);
  const targetColor = colors[0]!;
  const minOptimal = MIN_OPTIMAL[difficulty.name] ?? 2;

  for (let attempt = 0; attempt < 200; attempt++) {
    const grid = generateRandomGrid(rng, difficulty, colors, targetColor);

    if (checkWin(grid)) continue;

    const solution = bfsSolve(grid, difficulty.optimalDrops);

    // Accept if optimal solution meets the minimum threshold for this tier
    if (solution && solution.length >= minOptimal && solution.length <= difficulty.optimalDrops) {
      return {
        id: `spillover-${seed}`,
        seed,
        grid,
        targetColor,
        optimalMoves: solution.length,
        maxMoves: solution.length + 5,
        difficulty,
      };
    }
  }

  // Fallback — only reached during client-side generation; pre-gen script retries with different seeds
  return createFallback(seed, difficulty, colors, targetColor, rng);
}

// ── Fallback (safety net for client-side path) ──

function createFallback(
  seed: number,
  difficulty: PuzzleDifficulty,
  colors: PoolColor[],
  targetColor: PoolColor,
  rng: () => number
): Puzzle {
  const coords = hexRange(difficulty.radius);
  const cells = new Map<string, HexCell>();

  for (const coord of coords) {
    const isTarget = rng() < 0.6;
    cells.set(coordToKey(coord), {
      coord,
      color: isTarget ? targetColor : randPick(rng, colors.filter(c => c !== targetColor)),
      count: isTarget ? 1 : 2,
      capacity: 3,
    });
  }

  return {
    id: `spillover-${seed}`,
    seed,
    grid: { radius: difficulty.radius, cells, targetColor },
    targetColor,
    optimalMoves: difficulty.optimalDrops,
    maxMoves: difficulty.optimalDrops + 5,
    difficulty,
  };
}
